import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { Project } from '../../../models/Project/Project';
import { Col, Row, FormGroup, FormLabel } from 'react-bootstrap';
import { ButtonPrimary } from '../../components/Button/ButtonPrimary';
import { ErrorMessageLabel } from '../../components/ErrorMessageLabel/ErrorMessageLabel';
import { InputField } from '../../components/InputField/InputField';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router-dom';
import style from './create-project.module.scss';

function EditProject() {
  const { id } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();

  const { data: project, isLoading } = useQuery(
    ['project', id],
    () => Project.get(Number(id)),
    { enabled: typeof id !== 'undefined' }
  );

  if (isLoading) {
    return <h1>Loading...</h1>;
  }

  return (
    <div className={style.formWrapper}>
      <ToastContainer position="top-right" autoClose={3000} />
      <h1 className="cs-title">Promeni Projekat</h1>
      <Row>
        <Formik
          enableReinitialize
          initialValues={{
            name: project?.name ?? '',
            price: project?.price ?? 0,
            duration: project?.duration ?? 1,
            points_required: project?.points_required ?? 0
          }}
          validationSchema={Yup.object({
            name: Yup.string().required('Ime je obavezno'),
            price: Yup.number()
              .required('Cena je obavezna')
              .min(0, 'Cena ne može biti negativna'),
            duration: Yup.number()
              .required('Trajanje je obavezno')
              .min(1, 'Trajanje mora biti barem 1'),
            points_required: Yup.number()
              .required('Poeni su obavezni')
              .min(0, 'Poeni ne mogu biti negativni')
          })}
          onSubmit={async (values, { setSubmitting }) => {
            setSubmitting(true);
            try {
              await project?.update(values);
              toast.success('Projekat uspešno izmenjen!');
              await client.invalidateQueries({ queryKey: ['projects'] });
              await client.invalidateQueries({ queryKey: ['project', id] });
              navigate('/admin/projects/list');
            } catch (error: any) {
              toast.error(
                error?.response?.data?.message || 'Greška pri snimanju.'
              );
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form autoComplete="off" className="cs-form-wrapper">
              <FormGroup>
                <Col lg={6}>
                  <FormLabel>Ime</FormLabel>
                  <InputField
                    name="name"
                    placeholder="Unesite ime"
                    type="text"
                  />
                  <ErrorMessageLabel name="name" />
                </Col>
              </FormGroup>
              <FormGroup>
                <Col lg={6}>
                  <FormLabel>Cena</FormLabel>
                  <InputField
                    name="price"
                    placeholder="Unesite cenu"
                    type="number"
                    step="0.01"
                  />
                  <ErrorMessageLabel name="price" />
                </Col>
              </FormGroup>
              <FormGroup>
                <Col lg={6}>
                  <FormLabel>Trajanje</FormLabel>
                  <InputField
                    name="duration"
                    placeholder="Unesite trajanje"
                    type="number"
                    step="1"
                  />
                  <ErrorMessageLabel name="duration" />
                </Col>
              </FormGroup>
              <FormGroup>
                <Col lg={6}>
                  <FormLabel>Poeni</FormLabel>
                  <InputField
                    name="points_required"
                    placeholder="Unesite poene"
                    type="number"
                    step="1"
                  />
                  <ErrorMessageLabel name="points_required" />
                </Col>
              </FormGroup>
              <ButtonPrimary type="submit" disabled={isSubmitting}>
                Sačuvaj
              </ButtonPrimary>
            </Form>
          )}
        </Formik>
      </Row>
    </div>
  );
}

export default EditProject;
